import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CaseModal, { CaseDetail } from '@/components/feature/CaseModal';

const caseData: CaseDetail[] = [
  {
    id: 1,
    titleKey: 'cases.c1.title',
    typeKey: 'cases.c1.type',
    descKey: 'cases.c1.desc',
    tags: ['React', 'Node.js', 'Shopify'],
    img: 'https://readdy.ai/api/search-image?query=luxury%20fashion%20ecommerce%20website%20interface%20on%20laptop%20screen%2C%20elegant%20dark%20teal%20and%20gold%20color%20scheme%2C%20product%20grid%20layout%2C%20modern%20minimal%20design%2C%20high%20end%20retail%20digital%20experience%2C%20clean%20white%20background%20studio%20photography&width=600&height=800&seq=case1&orientation=portrait',
    large: true,
    year: '2024',
    featured: true,
    clientKey: 'cases.c1.client',
    challengeKey: 'cases.c1.challenge',
    solutionKey: 'cases.c1.solution',
    resultsKeys: ['cases.c1.result1', 'cases.c1.result2', 'cases.c1.result3', 'cases.c1.result4'],
  } as CaseDetail & { large: boolean },
  {
    id: 2,
    titleKey: 'cases.c2.title',
    typeKey: 'cases.c2.type',
    descKey: 'cases.c2.desc',
    tags: ['React Native', 'Supabase', 'AI'],
    img: 'https://readdy.ai/api/search-image?query=medical%20appointment%20mobile%20app%20interface%20on%20smartphone%2C%20clean%20white%20and%20teal%20color%20scheme%2C%20doctor%20patient%20booking%20UI%2C%20modern%20healthcare%20digital%20product%2C%20minimal%20flat%20design%2C%20warm%20cream%20background&width=800&height=400&seq=case2&orientation=landscape',
    large: false,
    year: '2024',
    clientKey: 'cases.c2.client',
    challengeKey: 'cases.c2.challenge',
    solutionKey: 'cases.c2.solution',
    resultsKeys: ['cases.c2.result1', 'cases.c2.result2', 'cases.c2.result3', 'cases.c2.result4'],
  } as CaseDetail & { large: boolean },
  {
    id: 3,
    titleKey: 'cases.c3.title',
    typeKey: 'cases.c3.type',
    descKey: 'cases.c3.desc',
    tags: ['Vue.js', 'Python', 'PostgreSQL'],
    img: 'https://readdy.ai/api/search-image?query=financial%20dashboard%20analytics%20software%20interface%20on%20desktop%20monitor%2C%20dark%20navy%20teal%20color%20scheme%20with%20amber%20gold%20charts%20and%20graphs%2C%20data%20visualization%20SaaS%20product%2C%20professional%20enterprise%20software%20UI%2C%20clean%20modern%20design&width=800&height=400&seq=case3&orientation=landscape',
    large: false,
    year: '2023',
    clientKey: 'cases.c3.client',
    challengeKey: 'cases.c3.challenge',
    solutionKey: 'cases.c3.solution',
    resultsKeys: ['cases.c3.result1', 'cases.c3.result2', 'cases.c3.result3', 'cases.c3.result4'],
  } as CaseDetail & { large: boolean },
];

type CaseDataItem = CaseDetail & { large: boolean };

function CaseCard({ c, index, onClick }: { c: CaseDataItem; index: number; onClick: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (c.large) {
    return (
      <div
        ref={ref}
        onClick={onClick}
        className={`relative rounded-3xl overflow-hidden group cursor-pointer transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
        style={{ transitionDelay: `${index * 120}ms` }}
      >
        <div className="w-full h-full min-h-[560px]">
          <img src={c.img} alt={t(c.titleKey)} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a4a]/90 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-7">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-500/80 text-white text-xs font-semibold mb-3">
            {t(c.typeKey)}
          </span>
          <h3 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t(c.titleKey)}
          </h3>
          <p className="text-white/75 text-sm leading-relaxed mb-4">{t(c.descKey)}</p>
          <div className="flex flex-wrap gap-2">
            {c.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-full bg-white/15 text-white/80 text-xs">{tag}</span>
            ))}
          </div>
        </div>
        <div className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <i className="ri-eye-line text-base"></i>
        </div>
        {/* Hover overlay hint */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-[#1e3a4a] text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            <span>{t('cases.modal.view')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`relative rounded-3xl overflow-hidden group cursor-pointer bg-white transition-all duration-700 hover:-translate-y-1 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="w-full h-52 overflow-hidden relative">
        <img src={c.img} alt={t(c.titleKey)} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-[#1e3a4a] text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            <span>{t('cases.modal.view')}</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-3">
          {t(c.typeKey)}
        </span>
        <h3 className="text-[#1e3a4a] text-lg font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t(c.titleKey)}
        </h3>
        <p className="text-[#666] text-sm leading-relaxed mb-4">{t(c.descKey)}</p>
        <div className="flex flex-wrap gap-2">
          {c.tags.map((tag) => (
            <span key={tag} className="px-2.5 py-1 rounded-full bg-[#f5f0e8] text-[#1e3a4a] text-xs font-medium">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CaseShowcase() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseDataItem | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cases" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1e3a4a]/8 border border-[#1e3a4a]/15 mb-5">
            <i className="ri-briefcase-4-line text-[#1e3a4a] text-sm"></i>
            <span className="text-[#1e3a4a] text-xs font-semibold tracking-wider uppercase">{t('cases.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a4a] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('cases.title')}
          </h2>
          <p className="text-[#666] text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            {t('cases.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CaseCard c={caseData[0] as CaseDataItem} index={0} onClick={() => setSelectedCase(caseData[0] as CaseDataItem)} />
          <div className="flex flex-col gap-6">
            <CaseCard c={caseData[1] as CaseDataItem} index={1} onClick={() => setSelectedCase(caseData[1] as CaseDataItem)} />
            <CaseCard c={caseData[2] as CaseDataItem} index={2} onClick={() => setSelectedCase(caseData[2] as CaseDataItem)} />
          </div>
        </div>

        <div className="text-center mt-14">
          <button
            onClick={() => navigate('/cases')}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-[#1e3a4a] text-[#1e3a4a] font-semibold text-sm hover:bg-[#1e3a4a] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <span>{t('cases.cta')}</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      <CaseModal
        caseItem={selectedCase}
        onClose={() => setSelectedCase(null)}
        getTitle={(c) => t(c.titleKey)}
        getType={(c) => t(c.typeKey)}
        getDesc={(c) => t(c.descKey)}
        getText={(key) => t(key)}
      />
    </section>
  );
}
